/**
 * 反復測定 ANOVA 専用 3列コンパクト表（被験体・因子・値）
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  repeatedColumnFormToGridData,
  gridDataToRepeatedColumnForm,
  type RepeatedColumnForm,
  normalizeKeisukeNumericInput,
} from '../../../../utils/keisukeInputFormUtils';
import {
  KEISUKE_GROUP_OBSERVATION_N_MAX,
  type DataRow,
} from '../../../../stores/systems/keisuke/dashboardStore';
import {
  clipboardLooksLikeSpreadsheetGrid,
  clipboardPlainTextFromEvent,
  parseClipboardTsvMatrix,
} from './keisukeDashboardClipboardUtils';
import { useDebouncedFormApply } from './useDebouncedFormApply';
import { useKeisukeThreeColumnGridClipboard } from './useKeisukeThreeColumnGridClipboard';
import {
  emptyThreeColumnRows,
  resizeThreeColumnRows,
  useKeisukeCompactFormResetOnNew,
} from './keisukeThreeColumnFormHelpers';
import styles from './KeisukeDashboardCenterPane.module.scss';
import grid from './KeisukeUnifiedDataGrid.module.scss';
import KeisukeDashboardGridObservationCountRow from './KeisukeDashboardGridObservationCountRow';
import KeisukeClipboardHintBar from './KeisukeClipboardHintBar';

export interface KeisukeDashboardDataFormRepeatedProps {
  gridData: DataRow[];
  gridHeaders: string[];
  subjectColumn?: string;
  factorColumn?: string;
  valueColumn?: string;
  onApply: (data: DataRow[], headers: string[]) => void;
  disabled?: boolean;
  nRows: number;
  onNRowsChange: (n: number) => void;
}

export default function KeisukeDashboardDataFormRepeated({
  gridData,
  gridHeaders,
  subjectColumn,
  factorColumn,
  valueColumn,
  onApply,
  disabled = false,
  nRows,
  onNRowsChange,
}: KeisukeDashboardDataFormRepeatedProps) {
  const { t } = useTranslation();
  const fieldIdPrefix = useId();
  const skipSyncRef = useRef(false);
  const suppressUntilRef = useRef(0);

  const buildEmptyForm = useCallback(
    (): RepeatedColumnForm => ({
      subjectHeader: t('keisuke.dashboard.inputForm.compactDefaultSubject'),
      factorHeader: t('keisuke.dashboard.inputForm.compactDefaultFactor'),
      valueHeader: t('keisuke.dashboard.inputForm.compactDefaultValue'),
      valueRows: emptyThreeColumnRows(nRows),
    }),
    [t, nRows]
  );

  const [form, setForm] = useState<RepeatedColumnForm>(() => {
    const fromGrid = gridDataToRepeatedColumnForm(
      gridData,
      gridHeaders,
      subjectColumn,
      factorColumn,
      valueColumn
    );
    if (fromGrid) return fromGrid;
    /** 空表は i18n 既定ヘッダのみ。前解析の mapping（group/value 等）で汚さない */
    return {
      subjectHeader: t('keisuke.dashboard.inputForm.compactDefaultSubject'),
      factorHeader: t('keisuke.dashboard.inputForm.compactDefaultFactor'),
      valueHeader: t('keisuke.dashboard.inputForm.compactDefaultValue'),
      valueRows: emptyThreeColumnRows(nRows),
    };
  });

  const clipboard = useKeisukeThreeColumnGridClipboard(form.valueRows, disabled);

  const prevNRef = useRef(nRows);
  useEffect(() => {
    if (prevNRef.current === nRows) return;
    prevNRef.current = nRows;
    setForm((prev) => ({ ...prev, valueRows: resizeThreeColumnRows(prev.valueRows, nRows) }));
  }, [nRows]);

  useEffect(() => {
    if (Date.now() < suppressUntilRef.current) return;
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    const fromGrid = gridDataToRepeatedColumnForm(
      gridData,
      gridHeaders,
      subjectColumn,
      factorColumn,
      valueColumn
    );
    if (fromGrid) {
      setForm(fromGrid);
      if (fromGrid.valueRows.length !== nRows) onNRowsChange(fromGrid.valueRows.length);
      return;
    }
    /** 新規などで headers が空のとき fromGrid は null → ローカル表を残さない */
    if (gridHeaders.length === 0) {
      setForm(buildEmptyForm());
    }
  }, [
    gridData,
    gridHeaders,
    subjectColumn,
    factorColumn,
    valueColumn,
    onNRowsChange,
    nRows,
    buildEmptyForm,
  ]);

  const resetToEmpty = useCallback(() => setForm(buildEmptyForm()), [buildEmptyForm]);
  useKeisukeCompactFormResetOnNew(resetToEmpty);

  const applyForm = useCallback(() => {
    const { headers, data } = repeatedColumnFormToGridData(form);
    suppressUntilRef.current = Date.now() + 950;
    skipSyncRef.current = true;
    onApply(data, headers);
  }, [form, onApply]);

  useDebouncedFormApply(applyForm, [form, applyForm], { delayMs: 280, skipInitial: true });

  const onPaste = useCallback(
    (e: React.ClipboardEvent, startRow: number, startCol: number) => {
      if (disabled) return;
      const text = clipboardPlainTextFromEvent(e);
      if (!clipboardLooksLikeSpreadsheetGrid(text)) return;
      e.preventDefault();
      const matrix = parseClipboardTsvMatrix(text);
      if (matrix.length === 0) return;
      setForm((prev) => {
        const nextRows = prev.valueRows.map((r) => [...r] as [string, string, string]);
        if (startRow < 0) {
          const headerLine = matrix[0] ?? [];
          const merged = {
            ...prev,
            subjectHeader: headerLine[0] ?? prev.subjectHeader,
            factorHeader: headerLine[1] ?? prev.factorHeader,
            valueHeader: headerLine[2] ?? prev.valueHeader,
          };
          const dataMatrix = matrix.slice(1);
          for (let i = 0; i < dataMatrix.length; i++) {
            const line = dataMatrix[i] ?? [];
            while (nextRows.length <= i) nextRows.push(['', '', '']);
            for (let j = 0; j < line.length && j < 3; j++) {
              const col = (startCol + j) as 0 | 1 | 2;
              const raw = String(line[j] ?? '');
              nextRows[i]![col] = col === 2 ? normalizeKeisukeNumericInput(raw) : raw;
            }
          }
          const out = { ...merged, valueRows: nextRows };
          if (out.valueRows.length > nRows) queueMicrotask(() => onNRowsChange(out.valueRows.length));
          queueMicrotask(applyForm);
          return out;
        }
        for (let i = 0; i < matrix.length; i++) {
          const r = startRow + i;
          while (nextRows.length <= r) nextRows.push(['', '', '']);
          const line = matrix[i] ?? [];
          for (let j = 0; j < line.length && startCol + j < 3; j++) {
            const col = (startCol + j) as 0 | 1 | 2;
            const raw = String(line[j] ?? '');
            nextRows[r]![col] = col === 2 ? normalizeKeisukeNumericInput(raw) : raw;
          }
        }
        const minLen = Math.max(nRows, startRow + matrix.length);
        while (nextRows.length < minLen) nextRows.push(['', '', '']);
        const out = { ...prev, valueRows: nextRows };
        if (out.valueRows.length > nRows) queueMicrotask(() => onNRowsChange(out.valueRows.length));
        queueMicrotask(applyForm);
        return out;
      });
    },
    [disabled, nRows, onNRowsChange, applyForm]
  );

  const setHeader = (key: 'subjectHeader' | 'factorHeader' | 'valueHeader', v: string) =>
    setForm((prev) => ({ ...prev, [key]: v }));

  const setCell = (row: number, col: 0 | 1 | 2, value: string) => {
    const v = col === 2 ? normalizeKeisukeNumericInput(value) : value;
    setForm((prev) => {
      const rows = prev.valueRows.map((line, ri) =>
        ri === row
          ? ([
              col === 0 ? v : line[0],
              col === 1 ? v : line[1],
              col === 2 ? v : line[2],
            ] as [string, string, string])
          : line
      );
      return { ...prev, valueRows: rows };
    });
  };

  const headerLabels = [
    { key: 'subjectHeader' as const, val: form.subjectHeader, col: 0 as const },
    { key: 'factorHeader' as const, val: form.factorHeader, col: 1 as const },
    { key: 'valueHeader' as const, val: form.valueHeader, col: 2 as const },
  ];

  return (
    <div className={`${styles.formWrap} ${styles.formWrapDense}`}>
      <KeisukeDashboardGridObservationCountRow
        label={t('keisuke.dashboard.inputForm.repeatedNRows')}
        value={nRows}
        onChange={onNRowsChange}
        onBlur={applyForm}
        disabled={disabled}
        min={2}
        max={KEISUKE_GROUP_OBSERVATION_N_MAX}
      />
      <div className={grid.keisukeAnovaTableWrap}>
        <div className={grid.tableScroll}>
          <table
            ref={clipboard.tableRef}
            className={grid.keisukeAnovaGridTable}
            onPointerDownCapture={clipboard.onTablePointerDownCapture}
            onCopyCapture={clipboard.onTableCopyCapture}
            onKeyDownCapture={clipboard.onTableKeyDownCapture}
          >
            <colgroup>
              <col className={grid.keisukeRowNumCol} />
              <col className={grid.keisukeAnovaColData} />
              <col className={grid.keisukeAnovaColData} />
              <col className={grid.keisukeAnovaColData} />
            </colgroup>
            <thead>
              <tr>
                <th className={grid.keisukeRowNumCell} aria-hidden>#</th>
                {headerLabels.map(({ key, val, col }) => (
                  <th key={key}>
                    <input
                      type="text"
                      className={grid.keisukeAnovaGridInputHeader}
                      id={`${fieldIdPrefix}-rm-h-${col}`}
                      value={val}
                      disabled={disabled}
                      onChange={(e) => setHeader(key, e.target.value)}
                      onBlur={applyForm}
                      onPaste={(e) => onPaste(e, -1, col)}
                      aria-label={t('keisuke.dashboard.columnNameAria', { column: val })}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {form.valueRows.map((row, ri) => (
                <tr key={ri}>
                  <td className={grid.keisukeRowNumCell} aria-hidden>{ri + 1}</td>
                  {([0, 1, 2] as const).map((ci) => (
                    <td key={ci} className={clipboard.gridCellClass(ri, ci)}>
                      <input
                        type="text"
                        className={grid.keisukeAnovaGridInputCell}
                        id={`${fieldIdPrefix}-rm-r${ri}-c${ci}`}
                        value={row[ci]}
                        disabled={disabled}
                        inputMode={ci === 2 ? 'decimal' : 'text'}
                        onChange={(e) => setCell(ri, ci, e.target.value)}
                        onBlur={applyForm}
                        onPaste={(e) => onPaste(e, ri, ci)}
                        aria-label={`${headerLabels[ci]!.val} ${ri + 1}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <KeisukeClipboardHintBar
        trailingHint={t('keisuke.dashboard.inputForm.repeatedCompactHint')}
      />
    </div>
  );
}
